import { useRef, type KeyboardEvent } from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type {
    AutocompleteHighlightChangeReason,
    AutocompleteInputChangeReason,
    FilterOptionsState
} from '@mui/material/useAutocomplete';

export type SuggestionFilter = (
    options: string[],
    state: FilterOptionsState<string>
) => string[];

/** MUI's own default, so a field without a filter of its own lists the same. */
const defaultFilter: SuggestionFilter = createFilterOptions<string>();

/**
 * The matches that START with what was typed, ahead of those that merely
 * contain it — the first match is the one Tab takes, and after "Holz" that
 * should be "Holz…", not "Brandholz". Stable otherwise, so a vocabulary keeps
 * its own order within each half.
 */
function prefixFirst(matches: string[], typed: string): string[] {
    const query = typed.trim().toLowerCase();
    if (!query) {
        return matches;
    }
    const starts = (option: string) => option.toLowerCase().startsWith(query);
    return [
        ...matches.filter(starts),
        ...matches.filter((option) => !starts(option))
    ];
}

/**
 * The keyboard rule of the free-text fields with suggestions, in forms and grid
 * cells alike:
 *
 * - Tab takes the HIGHLIGHTED suggestion — the first match is highlighted while
 *   typing, so "Ke" + Tab is "Keramik". Typed text that IS an option stays that
 *   option, wherever the list happens to put it.
 * - Enter takes what was TYPED — "Holz" stays "Holz" although "Holzkohle"
 *   exists. MUI already commits the typed text over its own highlight.
 * - An option picked with the arrow keys is a choice: Tab and Enter both take it.
 *
 * Tab takes nothing it was not asked to: not a highlight the mouse merely
 * passes over, not the first option of a list opened by a click without typing,
 * not anything into a field just emptied, and Shift+Tab and leaving by mouse
 * keep the typed text.
 *
 * MUI reports neither its automatic highlight nor the filtered list, so the
 * filter is wrapped to keep the last list, and the highlighted index is read
 * off the input's `aria-activedescendant`.
 */
export function useTabTakesSuggestion(
    filter: SuggestionFilter = defaultFilter
) {
    const filtered = useRef<string[]>([]);
    // Set by an explicit highlight, cleared by typing (MUI then highlights
    // the first match itself, which it does not report).
    const highlightReason = useRef<AutocompleteHighlightChangeReason | null>(
        null
    );
    const typed = useRef(false);

    // Not memoized: MUI filters on every render of an open list anyway, so a
    // stable `filterOptions` would save nothing.
    return {
        /** Spread onto the Autocomplete. */
        autocompleteProps: {
            autoHighlight: true,
            filterOptions: (
                options: string[],
                state: FilterOptionsState<string>
            ) => {
                filtered.current = prefixFirst(
                    filter(options, state),
                    state.inputValue
                );
                return filtered.current;
            },
            onHighlightChange: (
                _event: unknown,
                _option: unknown,
                reason: AutocompleteHighlightChangeReason
            ) => {
                highlightReason.current = reason;
            }
        },
        /** Call from the Autocomplete's `onInputChange`. */
        noteInput(reason: AutocompleteInputChangeReason) {
            typed.current = reason === 'input';
            highlightReason.current = null;
        },
        /**
         * Call from the Autocomplete's `onKeyDown`. Returns the suggestion this
         * key takes, or undefined when it takes none: Tab takes the highlight
         * (see above), Enter only one picked with the arrows — MUI selects that
         * too, but a caller that has to write the value before the key goes on
         * (a grid cell) needs to know.
         */
        keyDown(event: KeyboardEvent): string | undefined {
            const input = event.target as HTMLInputElement;
            const picked = highlightReason.current === 'keyboard';
            if (event.key === 'Enter') {
                return picked && !event.shiftKey
                    ? highlightedOption(input, filtered.current)
                    : undefined;
            }
            if (event.key !== 'Tab' || event.shiftKey) {
                return undefined;
            }
            if (picked) {
                return highlightedOption(input, filtered.current);
            }
            const text = input.value.trim();
            if (highlightReason.current !== null || !typed.current || !text) {
                return undefined;
            }
            const exact = filtered.current.find(
                (option) => option.toLowerCase() === text.toLowerCase()
            );
            return exact ?? highlightedOption(input, filtered.current);
        }
    };
}

/** The option the open list has highlighted, read off the input. */
function highlightedOption(
    input: HTMLElement,
    options: readonly string[]
): string | undefined {
    if (input.getAttribute('aria-expanded') !== 'true') {
        return undefined;
    }
    const active = input.getAttribute('aria-activedescendant');
    const index = Number(active?.match(/-option-(\d+)$/)?.[1] ?? NaN);
    return Number.isInteger(index) ? options[index] : undefined;
}
